import { Canvas, useFrame } from '@react-three/fiber'
import { useGLTF, MeshReflectorMaterial, BakeShadows } from '@react-three/drei'
import { EffectComposer, Bloom, DepthOfField } from '@react-three/postprocessing'
import { easing } from 'maath'
import { suspend } from 'suspend-react'
import { Instances, Computers } from './Computers'
import { useState, useEffect, useRef } from 'react'
import FullScreenOverlay from './components/FullScreenOverlay'
import AudioController from './components/AudioController'
import { useLoader } from '@react-three/fiber'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader'
import * as THREE from 'three'

const suzi = import('@pmndrs/assets/models/bunny.glb')

function CameraRig() {
  const [targetPosition, setTargetPosition] = useState([0, 1,5.5])
  const [targetLookAt, setTargetLookAt] = useState([0, 0, 0])

  useEffect(() => {
    const handleZoom = (event) => {
      if (event.detail.isZoomed) {
        // Position much closer to the computer screen
        setTargetPosition([
          event.detail.position.x, 
          event.detail.position.y, 
          event.detail.position.z + 0.2  // Changed from 0.5 to 0.2 to get much closer
        ])
        setTargetLookAt([
          event.detail.position.x, 
          event.detail.position.y, 
          event.detail.position.z
        ])
      } else {
        setTargetPosition([0, 1, 5.5])
        setTargetLookAt([0, 0, 0])
      }
    }

    window.addEventListener('zoomToComputer', handleZoom)
    return () => window.removeEventListener('zoomToComputer', handleZoom)
  }, [])

  useFrame((state) => {
    easing.damp3(state.camera.position, targetPosition, 0.15, state.delta)
    state.camera.lookAt(...targetLookAt)
  })
}

export default function App() {
  const [showFullScreen, setShowFullScreen] = useState(false)

  useEffect(() => {
    const handleZoom = (event) => {
      if (event.detail.isZoomed) {
        setTimeout(() => {
          setShowFullScreen(true)
        }, 2000)
      } else {
        setShowFullScreen(false)
      }
    }

    window.addEventListener('zoomToComputer', handleZoom)
    return () => window.removeEventListener('zoomToComputer', handleZoom)
  }, [])

  const handleExit = () => {
    window.dispatchEvent(new CustomEvent('zoomToComputer', { 
      detail: { isZoomed: false }
    }))
    setShowFullScreen(false)
  }

  return (
    <>
      <Canvas 
        shadows 
        dpr={[1, 1.2]}
        gl={{
          antialias: false, // We're using post-processing antialiasing
          powerPreference: "high-performance",
          alpha: false,
          stencil: false,
          depth: true
        }}
        camera={{ position: [-1.5, 1, 5.5], fov: 45, near: 1, far: 20 }} 
        eventSource={document.getElementById('root')} 
        eventPrefix="client"
      >
        <color attach="background" args={['black']} />
        <hemisphereLight intensity={0.15} groundColor="black" />
        <spotLight 
          decay={0} 
          position={[10, 20, 10]} 
          angle={0.12} 
          penumbra={1} 
          intensity={1} 
          castShadow 
          shadow-mapSize={512}
        />
        <group position={[-0.13, -1, 0]}>
          <Instances>
            <Computers scale={0.5} />
          </Instances>
          <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[50, 50]} />
            <MeshReflectorMaterial
              blur={[100, 30]}
              resolution={1024}
              mixBlur={1}
              mixStrength={180}
              roughness={1}
              depthScale={1.2}
              minDepthThreshold={0.4}
              maxDepthThreshold={1.4}
              color="#202020"
              metalness={0.8}
            />
          </mesh>
        </group>
        <EffectComposer disableNormalPass>
          <Bloom 
            luminanceThreshold={0} 
            mipmapBlur 
            luminanceSmoothing={0.0} 
            intensity={3}
          />
          <DepthOfField 
            target={[0, 0, 0.5]} 
            focalLength={0.5} 
            bokehScale={3}
            height={500}
          />
        </EffectComposer>
        <CameraRig />
        <BakeShadows />
      </Canvas>
      <AudioController showFullScreen={showFullScreen} />
      {showFullScreen && <FullScreenOverlay onClose={handleExit} />}
    </>
  )
}

function Bun(props) {
  const { nodes } = useGLTF(suspend(suzi).default)
  console.log(nodes)
  return (
    <mesh receiveShadow castShadow geometry={nodes.mesh.geometry} {...props}>
      <meshStandardMaterial color="#222" roughness={0.5} />
    </mesh>
  )
}
